// frontend/src/EvaluationGrid.tsx - Grid-based Comparative Evaluation Interface

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { API_URL } from './apiConfig';

interface EvaluationGridProps {
  processId: number;
  evaluatorId: number;
  userRole?: string;
  onBack: () => void;
  onComplete: () => void;
}

// --- Data Interfaces (matching existing EvaluationInterface) ---
interface ProcessResponse {
  id: number;
  name: string;
  description: string | null;
  ito_reference: string | null;
  status: string;
  score_scale: string;
  evaluation_methodology: string;
  created_at: string;
  updated_at: string;
}

interface MandatoryRequirementResponse {
  id: number;
  description: string;
  order_index: number;
}

interface SubCriterionResponse {
  id: number;
  name: string;
  description: string | null;
  weight_percentage: number;
  order_index: number;
}

interface MainCriterionResponse {
  id: number;
  name: string;
  description: string | null;
  weight_percentage: number;
  order_index: number;
  sub_criteria: SubCriterionResponse[];
}

interface SupplierResponse {
  id: number;
  name: string;
  abn_acn: string;
  total_offered_price: number | null;
  created_at: string;
}

interface CompleteProcessDataResponse {
  process: ProcessResponse;
  mandatory_requirements: MandatoryRequirementResponse[];
  main_criteria: MainCriterionResponse[];
  suppliers: SupplierResponse[];
}

// --- Evaluation State Interfaces ---
interface MandatoryEvaluation {
  supplier_id: number;
  requirement_id: number;
  is_compliant: boolean;
  comments: string;
}

interface SubCriterionEvaluation {
  supplier_id: number;
  sub_criterion_id: number;
  score: number | null;
  comments: string;
}

interface GridState {
  expandedCriteria: Set<number>; // Main criteria that show sub-criteria
  collapsedSuppliers: Set<number>; // Suppliers that are collapsed to status indicators
  expandedComments: Set<string>; // Format: "supplierId-criterionId"
  currentEditingComment: string | null;
  stickyHeaders: boolean;
}

interface CellState {
  isHovered: boolean;
  isEditing: boolean;
  showTooltip: boolean;
}

const EvaluationGrid: React.FC<EvaluationGridProps> = ({
  processId,
  evaluatorId,
  userRole,
  onBack,
  onComplete,
}) => {
  // --- Data State ---
  const [process, setProcess] = useState<ProcessResponse | null>(null);
  const [suppliers, setSuppliers] = useState<SupplierResponse[]>([]);
  const [mandatoryRequirements, setMandatoryRequirements] = useState<MandatoryRequirementResponse[]>([]);
  const [mainCriteria, setMainCriteria] = useState<MainCriterionResponse[]>([]);

  // --- Evaluation State ---
  const [mandatoryEvaluations, setMandatoryEvaluations] = useState<MandatoryEvaluation[]>([]);
  const [subCriterionEvaluations, setSubCriterionEvaluations] = useState<SubCriterionEvaluation[]>([]);

  // --- UI State ---
  const [gridState, setGridState] = useState<GridState>({
    expandedCriteria: new Set(mainCriteria.map(mc => mc.id)), // All criteria expanded by default
    collapsedSuppliers: new Set(), // No suppliers collapsed by default
    expandedComments: new Set(),
    currentEditingComment: null,
    stickyHeaders: true,
  });
  const [cellStates, setCellStates] = useState<Record<string, CellState>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  // --- Utility Functions ---
  const showNotification = useCallback((type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 3000);
  }, []);

  const getScoreDefinitions = useCallback(() => {
    if (!process) return [];
    
    if (process.score_scale === "0-10") {
      return [
        { score: 0, title: "Non-compliant/No response" },
        { score: 1, title: "Extremely Poor" },
        { score: 2, title: "Very Poor" },
        { score: 3, title: "Poor" },
        { score: 4, title: "Below Average" },
        { score: 5, title: "Meets minimum" },
        { score: 6, title: "Slightly above minimum" },
        { score: 7, title: "Above minimum, some exceeds" },
        { score: 8, title: "Majority exceeds" },
        { score: 9, title: "Entire response exceeds" },
        { score: 10, title: "Exceptional and outstanding" }
      ];
    } else {
      return [
        { score: 0, title: "Non-compliant/No response" },
        { score: 1, title: "Poor" },
        { score: 2, title: "Below expectations" },
        { score: 3, title: "Meets expectations" },
        { score: 4, title: "Exceeds expectations" },
        { score: 5, title: "Outstanding" }
      ];
    }
  }, [process]);

  // --- Interactive Scoring Functions ---
  const updateMandatoryEvaluation = useCallback((supplierId: number, requirementId: number, field: keyof MandatoryEvaluation, value: any) => {
    setMandatoryEvaluations(prev =>
      prev.map(evaluation =>
        evaluation.supplier_id === supplierId && evaluation.requirement_id === requirementId
          ? { ...evaluation, [field]: value }
          : evaluation
      )
    );
  }, []);

  const updateSubCriterionEvaluation = useCallback((supplierId: number, subCriterionId: number, field: keyof SubCriterionEvaluation, value: any) => {
    setSubCriterionEvaluations(prev =>
      prev.map(evaluation =>
        evaluation.supplier_id === supplierId && evaluation.sub_criterion_id === subCriterionId
          ? { ...evaluation, [field]: value }
          : evaluation
      )
    );
  }, []);

  const toggleCommentExpansion = useCallback((supplierId: number, criterionId: number) => {
    const key = `${supplierId}-${criterionId}`;
    setGridState(prev => {
      const newExpandedComments = new Set(prev.expandedComments);
      if (newExpandedComments.has(key)) {
        newExpandedComments.delete(key);
      } else {
        newExpandedComments.add(key);
      }
      return { ...prev, expandedComments: newExpandedComments };
    });
  }, []);
  
  const toggleCriteriaExpansion = useCallback((criterionId: number) => {
    setGridState(prev => {
      const newExpandedCriteria = new Set(prev.expandedCriteria);
      if (newExpandedCriteria.has(criterionId)) {
        newExpandedCriteria.delete(criterionId);
      } else {
        newExpandedCriteria.add(criterionId);
      }
      return { ...prev, expandedCriteria: newExpandedCriteria };
    });
  }, []);
  
  const toggleSupplierCollapse = useCallback((supplierId: number) => {
    setGridState(prev => {
      const newCollapsedSuppliers = new Set(prev.collapsedSuppliers);
      if (newCollapsedSuppliers.has(supplierId)) {
        newCollapsedSuppliers.delete(supplierId);
      } else {
        newCollapsedSuppliers.add(supplierId);
      }
      return { ...prev, collapsedSuppliers: newCollapsedSuppliers };
    });
  }, []);

  const updateCellState = useCallback((key: string, updates: Partial<CellState>) => {
    setCellStates(prev => ({
      ...prev,
      [key]: { ...prev[key], ...updates }
    }));
  }, []);

  const getMandatoryEvaluation = useCallback((supplierId: number, requirementId: number) => {
    return mandatoryEvaluations.find(evaluation => 
      evaluation.supplier_id === supplierId && evaluation.requirement_id === requirementId
    );
  }, [mandatoryEvaluations]);

  const getSubCriterionEvaluation = useCallback((supplierId: number, subCriterionId: number) => {
    return subCriterionEvaluations.find(evaluation => 
      evaluation.supplier_id === supplierId && evaluation.sub_criterion_id === subCriterionId
    );
  }, [subCriterionEvaluations]);
  
  // Calculate supplier completion status for collapsed view
  const getSupplierCompletionStatus = useCallback((supplierId: number) => {
    const totalMandatory = mandatoryRequirements.length;
    const totalScores = mainCriteria.reduce((total, mc) => total + mc.sub_criteria.length, 0);
    
    const completedMandatory = mandatoryEvaluations.filter(e => 
      e.supplier_id === supplierId && e.comments && e.comments.trim() !== ''
    ).length;
    
    const completedScores = subCriterionEvaluations.filter(e => 
      e.supplier_id === supplierId && e.score !== null && e.comments && e.comments.trim() !== ''
    ).length;
    
    const totalRequired = totalMandatory + totalScores;
    const totalCompleted = completedMandatory + completedScores;
    const percentage = totalRequired > 0 ? Math.round((totalCompleted / totalRequired) * 100) : 0;
    
    return {
      completed: totalCompleted,
      total: totalRequired,
      percentage,
      mandatoryCompleted: completedMandatory,
      mandatoryTotal: totalMandatory,
      scoresCompleted: completedScores,
      scoresTotal: totalScores
    };
  }, [mandatoryRequirements, mainCriteria, mandatoryEvaluations, subCriterionEvaluations]);

  // --- API Integration Functions ---
  const saveEvaluations = useCallback(async (isComplete: boolean = false) => {
    if (!process || !evaluatorId) {
      showNotification('error', 'Cannot save: missing process or evaluator data');
      return false;
    }

    setIsSaving(true);
    try {
      const token = Cookies.get('access_token');
      const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

      // Save evaluations for each supplier
      const savePromises = suppliers.map(async (supplier) => {
        // FIXED: For completion mode, validate mandatory evaluations have comments first
        const validMandatoryEvaluations = mandatoryEvaluations
          .filter(evaluation => 
            evaluation.supplier_id === supplier.id &&
            evaluation.comments &&
            evaluation.comments.trim() !== ''
          );

        // FIXED: Use validated mandatory evaluations based on save mode
        const supplierMandatory = (isComplete ? 
          validMandatoryEvaluations :
          mandatoryEvaluations.filter(evaluation => evaluation.supplier_id === supplier.id)
        ).map(evaluation => ({
            requirement_id: evaluation.requirement_id,
            is_compliant: evaluation.is_compliant,
            comments: evaluation.comments || ''
          }));

        // FIXED: Prepare sub-criterion scores with proper validation
        const supplierScores = subCriterionEvaluations
          .filter(evaluation => 
            evaluation.supplier_id === supplier.id && 
            evaluation.score !== null && 
            evaluation.score !== undefined &&
            evaluation.comments && 
            evaluation.comments.trim() !== ''
          )
          .map(evaluation => ({
            sub_criterion_id: evaluation.sub_criterion_id,
            score: evaluation.score!,
            comments: evaluation.comments
          }));

        // FIXED: Enhanced validation before saving
        if (isComplete) {
          // For completion, ensure ALL required data is present
          const requiredMandatoryCount = mandatoryRequirements.length;
          const requiredScoreCount = mainCriteria.reduce((total, mc) => total + mc.sub_criteria.length, 0);
          
          if (supplierMandatory.length < requiredMandatoryCount || supplierScores.length < requiredScoreCount) {
            throw new Error(`Incomplete data for supplier ${supplier.name}: Need ${requiredMandatoryCount} mandatory evaluations and ${requiredScoreCount} scores`);
          }
        }
        
        // Only save if there are evaluations to save
        if (supplierMandatory.length > 0 || supplierScores.length > 0) {
          const payload = {
            process_id: process.id,
            supplier_id: supplier.id,
            evaluator_id: evaluatorId,
            mandatory_evaluations: supplierMandatory,
            sub_criterion_scores: supplierScores,
            is_complete: isComplete
          };

          await axios.post(`${API_URL}/api/evaluations/`, payload, authHeaders);
        }
      });

      await Promise.all(savePromises);
      setLastSaved(new Date());
      showNotification('success', isComplete ? 'Evaluations completed successfully!' : 'Progress saved successfully!');
      return true;
    } catch (error: any) {
      console.error('Error saving evaluations:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to save evaluations';
      showNotification('error', errorMessage);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [process, evaluatorId, suppliers, mandatoryEvaluations, subCriterionEvaluations, showNotification]);

  const handleCompleteEvaluation = useCallback(async () => {
    // Validate that all evaluations have comments
    const mandatoryMissingComments = mandatoryEvaluations.filter(evaluation => 
      !evaluation.comments || evaluation.comments.trim() === ''
    );
    
    const scoresMissingComments = subCriterionEvaluations.filter(evaluation => 
      evaluation.score !== null && (!evaluation.comments || evaluation.comments.trim() === '')
    );

    if (mandatoryMissingComments.length > 0) {
      const missingList = mandatoryMissingComments.slice(0, 3).map(evaluation => {
        const req = mandatoryRequirements.find(r => r.id === evaluation.requirement_id);
        return req?.description || 'Unknown requirement';
      }).join('\n• ');
      
      const message = `Cannot complete evaluation. Missing comments for mandatory requirements:\n\n• ${missingList}${mandatoryMissingComments.length > 3 ? `\n• ...and ${mandatoryMissingComments.length - 3} more requirements` : ''}\n\nAll evaluations must include detailed justification comments.`;
      alert(message);
      return;
    }

    if (scoresMissingComments.length > 0) {
      const missingList = scoresMissingComments.slice(0, 3).map(evaluation => {
        // Find the criterion and supplier for this evaluation
        let criterionName = 'Unknown criterion';
        let supplierName = 'Unknown supplier';
        
        mainCriteria.forEach(mc => {
          const subCrit = mc.sub_criteria?.find(sc => sc.id === evaluation.sub_criterion_id);
          if (subCrit) criterionName = `${mc.name} → ${subCrit.name}`;
        });
        
        const supplier = suppliers.find(s => s.id === evaluation.supplier_id);
        if (supplier) supplierName = supplier.name;
        
        return `${supplierName}: ${criterionName}`;
      }).join('\n• ');
      
      const message = `Cannot complete evaluation. Missing comments for scored criteria:\n\n• ${missingList}${scoresMissingComments.length > 3 ? `\n• ...and ${scoresMissingComments.length - 3} more criteria` : ''}\n\nAll scores must include detailed justification comments.`;
      alert(message);
      return;
    }

    const success = await saveEvaluations(true);
    if (success) {
      setTimeout(() => onComplete(), 2000);
    }
  }, [mandatoryEvaluations, subCriterionEvaluations, saveEvaluations, showNotification, onComplete]);

  // FIXED: Enhanced auto-save with proper completion tracking
  const [lastSavedState, setLastSavedState] = React.useState<string>('');
  const [evaluationProgress, setEvaluationProgress] = React.useState({ completed: 0, total: 0, percentage: 0 });
  
  // Track evaluation progress
  useEffect(() => {
    if (process && suppliers.length > 0) {
      const totalRequired = suppliers.length * (
        mandatoryRequirements.length + 
        mainCriteria.reduce((total, mc) => total + mc.sub_criteria.length, 0)
      );
      
      const completedMandatory = mandatoryEvaluations.filter(e => 
        e.comments && e.comments.trim() !== ''
      ).length;
      
      const completedScores = subCriterionEvaluations.filter(e => 
        e.score !== null && e.comments && e.comments.trim() !== ''
      ).length;
      
      const completed = completedMandatory + completedScores;
      const percentage = totalRequired > 0 ? Math.round((completed / totalRequired) * 100) : 0;
      
      setEvaluationProgress({ completed, total: totalRequired, percentage });
    }
  }, [process, suppliers, mandatoryRequirements, mainCriteria, mandatoryEvaluations, subCriterionEvaluations]);
  
  useEffect(() => {
    if (!isLoading && process) {
      const autoSaveInterval = setInterval(() => {
        // FIXED: Enhanced state tracking including completion status
        const currentState = JSON.stringify({
          mandatory: mandatoryEvaluations.map(e => ({ 
            id: `${e.supplier_id}-${e.requirement_id}`,
            is_compliant: e.is_compliant,
            comments: e.comments,
            hasComment: !!(e.comments && e.comments.trim())
          })),
          subcriteria: subCriterionEvaluations.map(e => ({ 
            id: `${e.supplier_id}-${e.sub_criterion_id}`,
            score: e.score, 
            comments: e.comments,
            isComplete: e.score !== null && e.comments && e.comments.trim() !== ''
          })),
          progress: evaluationProgress
        });
        
        // Save if state has changed (including when fields are cleared/added)
        if (currentState !== lastSavedState && !isSaving) {
          // FIXED: Never auto-save as complete - only manual completion allowed
          saveEvaluations(false).then((success) => {
            if (success) {
              setLastSavedState(currentState);
            }
          }).catch(() => {
            // If auto-save fails, don't update lastSavedState so it will retry
            console.log('Auto-save failed, will retry next interval');
          });
        }
      }, 30000); // Auto-save every 30 seconds

      return () => clearInterval(autoSaveInterval);
    }
  }, [isLoading, process, mandatoryEvaluations, subCriterionEvaluations, evaluationProgress, isSaving, saveEvaluations, lastSavedState]);
  
  // Initialize expanded criteria when main criteria load
  useEffect(() => {
    if (mainCriteria.length > 0 && gridState.expandedCriteria.size === 0) {
      setGridState(prev => ({
        ...prev,
        expandedCriteria: new Set(mainCriteria.map(mc => mc.id))
      }));
    }
  }, [mainCriteria, gridState.expandedCriteria.size]);

  // --- Data Loading ---
  useEffect(() => {
    const loadEvaluationData = async () => {
      setIsLoading(true);
      try {
        const token = Cookies.get('access_token');
        const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

        // Load process data
        const processDataResponse = await axios.get<CompleteProcessDataResponse>(
          `${API_URL}/api/processes/${processId}/complete-data`,
          authHeaders
        );
        const data = processDataResponse.data;

        setProcess(data.process);
        setSuppliers(data.suppliers);
        setMandatoryRequirements(data.mandatory_requirements);
        setMainCriteria(data.main_criteria);

        // Initialize evaluation state for all suppliers
        const initialMandatory: MandatoryEvaluation[] = [];
        const initialSubCriteria: SubCriterionEvaluation[] = [];

        data.suppliers.forEach(supplier => {
          // Initialize mandatory evaluations
          data.mandatory_requirements.forEach(requirement => {
            initialMandatory.push({
              supplier_id: supplier.id,
              requirement_id: requirement.id,
              is_compliant: true, // Default to compliant
              comments: ''
            });
          });

          // Initialize sub-criteria evaluations
          data.main_criteria.forEach(criterion => {
            criterion.sub_criteria.forEach(subCriterion => {
              initialSubCriteria.push({
                supplier_id: supplier.id,
                sub_criterion_id: subCriterion.id,
                score: null,
                comments: ''
              });
            });
          });
        });
        
        // Initialize cell states
        const initialCellStates: Record<string, CellState> = {};
        data.suppliers.forEach(supplier => {
          data.mandatory_requirements.forEach(requirement => {
            const key = `mandatory-${supplier.id}-${requirement.id}`;
            initialCellStates[key] = { isHovered: false, isEditing: false, showTooltip: false };
          });
          data.main_criteria.forEach(criterion => {
            criterion.sub_criteria.forEach(subCriterion => {
              const key = `score-${supplier.id}-${subCriterion.id}`;
              initialCellStates[key] = { isHovered: false, isEditing: false, showTooltip: false };
            });
          });
        });
        setCellStates(initialCellStates);

        setMandatoryEvaluations(initialMandatory);
        setSubCriterionEvaluations(initialSubCriteria);

        // Load existing evaluations for this evaluator
        try {
          const existingEvaluations = await Promise.all(
            data.suppliers.map(async (supplier) => {
              try {
                const response = await axios.get(
                  `${API_URL}/api/evaluations/${processId}/${supplier.id}/${evaluatorId}`,
                  authHeaders
                );
                return { supplierId: supplier.id, data: response.data };
              } catch (error) {
                // 404 is expected for new evaluations
                return { supplierId: supplier.id, data: null };
              }
            })
          );

          // Update evaluations with existing data
          existingEvaluations.forEach(({ supplierId, data: existingData }) => {
            if (existingData?.exists && existingData.evaluation_result) {
              // Update mandatory evaluations
              existingData.mandatory_evaluations.forEach((existing: any) => {
                const index = initialMandatory.findIndex(
                  evaluation => evaluation.supplier_id === supplierId && evaluation.requirement_id === existing.requirement_id
                );
                if (index >= 0) {
                  initialMandatory[index].is_compliant = existing.is_compliant;
                  initialMandatory[index].comments = existing.comments || '';
                }
              });

              // Update sub-criterion evaluations
              existingData.sub_criterion_scores.forEach((existing: any) => {
                const index = initialSubCriteria.findIndex(
                  evaluation => evaluation.supplier_id === supplierId && evaluation.sub_criterion_id === existing.sub_criterion_id
                );
                if (index >= 0) {
                  initialSubCriteria[index].score = existing.score;
                  initialSubCriteria[index].comments = existing.comments || '';
                }
              });
            }
          });
          
          showNotification('success', 'Evaluation data loaded successfully');
        } catch (error) {
          console.error('Error loading existing evaluations:', error);
          showNotification('success', 'New evaluation session started');
        }
      } catch (error) {
        console.error('Error loading evaluation data:', error);
        showNotification('error', 'Failed to load evaluation data');
      } finally {
        setIsLoading(false);
      }
    };

    loadEvaluationData();
  }, [processId, showNotification]);

  // --- Desktop-Only Design (Mobile removed for professional use case) ---
  const [screenTooSmall, setScreenTooSmall] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setScreenTooSmall(window.innerWidth < 1024);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // --- Keyboard Navigation ---
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Close any expanded comments
        setGridState(prev => ({ ...prev, expandedComments: new Set() }));
      }
      if (event.ctrlKey || event.metaKey) {
        if (event.key === 's') {
          event.preventDefault();
          saveEvaluations(false);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [saveEvaluations]);

  // --- Grid Styling ---
  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column' as const,
      minHeight: '100vh',
      background: '#f9fafb',
    },
    header: {
      background: 'white',
      padding: '1.5rem 2rem',
      borderBottom: '1px solid #e5e7eb',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: 'bold',
      color: '#1f2937',
      margin: '0 0 0.5rem 0',
    },
    subtitle: {
      color: '#6b7280',
      margin: 0,
    },
    gridContainer: {
      flex: 1,
      overflow: 'auto',
      padding: '1rem',
      paddingBottom: '3rem',
    },
    gridTable: {
      display: 'grid',
      gridTemplateColumns: `300px ${suppliers.map(supplier => 
        gridState.collapsedSuppliers.has(supplier.id) ? '60px' : 'minmax(240px, 1fr)'
      ).join(' ')}`,
      gap: '1px',
      background: '#e5e7eb',
      borderRadius: '8px',
      overflow: 'hidden',
      minWidth: `${300 + suppliers.reduce((width, supplier) => 
        width + (gridState.collapsedSuppliers.has(supplier.id) ? 60 : 240), 0
      )}px`,
    },
    headerCell: {
      background: '#374151',
      color: 'white',
      padding: '1rem',
      fontWeight: 'bold',
      fontSize: '0.9rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center' as const,
    },
    criteriaHeader: {
      background: '#374151',
      color: 'white',
      padding: '1rem',
      fontWeight: 'bold',
      fontSize: '0.9rem',
      display: 'flex',
      alignItems: 'center',
    },
    criteriaCell: {
      background: 'white',
      padding: '1rem',
      borderRight: '1px solid #e5e7eb',
      display: 'flex',
      alignItems: 'center',
      fontWeight: '600',
      color: '#374151',
    },
    scoreCell: {
      background: 'white',
      padding: '0.875rem',
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '70px',
      border: '2px solid transparent',
      transition: 'all 0.2s ease',
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '400px',
      fontSize: '1.1rem',
      color: '#6b7280',
    },
    backButton: {
      background: '#6b7280',
      color: 'white',
      border: 'none',
      padding: '0.75rem 1.5rem',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '0.9rem',
      fontWeight: '600',
      transition: 'background 0.2s ease',
    },
  };

  // --- Loading State ---
  if (isLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div>Loading evaluation data...</div>
          <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '1rem' }}>
            Professional procurement evaluation - Desktop/laptop recommended
          </div>
        </div>
      </div>
    );
  }

  if (!process || suppliers.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div>No evaluation data found.</div>
          <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '1rem' }}>
            This interface is optimized for desktop and laptop computers
          </div>
        </div>
      </div>
    );
  }

  // --- Screen Size Check ---
  if (screenTooSmall) {
    return (
      <div style={styles.container}>
        <div style={{
          ...styles.loadingContainer,
          textAlign: 'center',
          padding: '3rem 2rem'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1.5rem' }}>🖥️</div>
          <h2 style={{ color: '#1e293b', marginBottom: '1rem' }}>Desktop Required</h2>
          <p style={{ color: '#6b7280', marginBottom: '1.5rem', maxWidth: '400px', margin: '0 auto 1.5rem auto', lineHeight: '1.6' }}>
            EvaluTool's Grid Evaluation Interface is designed for professional procurement evaluation on desktop and laptop computers.
          </p>
          <div style={{ 
            background: '#fef3c7',
            border: '1px solid #f59e0b',
            borderRadius: '8px',
            padding: '1rem',
            maxWidth: '500px',
            margin: '0 auto 2rem auto'
          }}>
            <p style={{ margin: 0, fontSize: '0.9rem', color: '#92400e' }}>
              <strong>Minimum Requirements:</strong><br/>
              • Screen width: 1024px or larger<br/>
              • Desktop or laptop computer<br/>
              • Mouse or trackpad for optimal interaction
            </p>
          </div>
          <button
            onClick={onBack}
            style={{
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: '600'
            }}
          >
            ← Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // --- Render Grid ---
  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1 style={styles.title}>
              Grid Evaluation: {process.name}
            </h1>
            <p style={styles.subtitle}>
              {process.evaluation_methodology?.toUpperCase()} • {process.score_scale} Scale • {suppliers.length} Suppliers
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.5rem' }}>
            {/* FIXED: Progress indicator for evaluation completion */}
            <div style={{
              fontSize: '0.8rem',
              color: evaluationProgress.percentage === 100 ? '#059669' : '#d97706',
              backgroundColor: evaluationProgress.percentage === 100 ? '#dcfce7' : '#fef3c7',
              padding: '0.5rem 0.75rem',
              borderRadius: '6px',
              border: `1px solid ${evaluationProgress.percentage === 100 ? '#10b981' : '#f59e0b'}`,
              textAlign: 'right',
              fontWeight: '600'
            }}>
              📊 Progress: {evaluationProgress.percentage}%
              <div style={{ fontSize: '0.7rem', fontWeight: 'normal', opacity: 0.8 }}>
                {evaluationProgress.completed}/{evaluationProgress.total} items complete
              </div>
            </div>
            <div style={{ 
              fontSize: '0.75rem', 
              color: '#6b7280', 
              textAlign: 'right',
              backgroundColor: '#f3f4f6',
              padding: '0.5rem 0.75rem',
              borderRadius: '6px',
              border: '1px solid #d1d5db'
            }}>
              🖥️ Desktop Optimized
              <div>Min. 1024px width recommended</div>
            </div>
          </div>
        </div>
      </div>

      {/* Grid Container */}
      <div style={styles.gridContainer}>
        <div style={styles.gridTable}>
          {/* Header Row */}
          <div style={styles.criteriaHeader}>
            Criteria
          </div>
          {suppliers.map(supplier => {
            const isCollapsed = gridState.collapsedSuppliers.has(supplier.id);
            const completionStatus = getSupplierCompletionStatus(supplier.id);
            
            return (
              <div 
                key={supplier.id} 
                style={{
                  ...styles.headerCell,
                  cursor: 'pointer',
                  background: isCollapsed 
                    ? (completionStatus.percentage === 100 ? '#065f46' : completionStatus.percentage > 50 ? '#d97706' : '#dc2626')
                    : '#374151',
                  transition: 'all 0.2s ease'
                }}
                onClick={() => toggleSupplierCollapse(supplier.id)}
                title={isCollapsed 
                  ? `${supplier.name}: ${completionStatus.percentage}% complete (click to expand)`
                  : 'Click to collapse this supplier column'}
              >
                {isCollapsed ? (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '0.7rem', fontWeight: 'bold' }}>
                      {completionStatus.percentage}%
                    </div>
                    <div style={{ fontSize: '0.6rem', opacity: 0.9, marginTop: '0.25rem' }}>
                      {completionStatus.completed}/{completionStatus.total}
                    </div>
                  </div>
                ) : (
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '1rem' }}>
                      {supplier.name}
                    </div>
                    <div style={{ fontSize: '0.75rem', opacity: 0.8, marginTop: '0.25rem' }}>
                      ABN: {supplier.abn_acn}
                    </div>
                    <div style={{ fontSize: '0.7rem', opacity: 0.9, marginTop: '0.25rem' }}>
                      📊 {completionStatus.percentage}% complete
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Mandatory Requirements Section */}
          {mandatoryRequirements.length > 0 && (
            <>
              <div style={{ ...styles.criteriaCell, background: '#fef3c7', fontWeight: 'bold' }}>
                📋 Mandatory Requirements
              </div>
              {suppliers.map(supplier => {
                const isCollapsed = gridState.collapsedSuppliers.has(supplier.id);
                const completionStatus = getSupplierCompletionStatus(supplier.id);
                
                return (
                  <div key={`mandatory-${supplier.id}`} style={{ 
                    ...styles.scoreCell, 
                    background: isCollapsed 
                      ? (completionStatus.mandatoryCompleted === completionStatus.mandatoryTotal ? '#dcfce7' : '#fef2f2')
                      : '#fef3c7',
                    padding: isCollapsed ? '0.5rem 0.25rem' : '0.875rem'
                  }}>
                    {isCollapsed ? (
                      <div style={{ textAlign: 'center', fontSize: '0.7rem', color: '#92400e' }}>
                        {completionStatus.mandatoryCompleted}/{completionStatus.mandatoryTotal}
                      </div>
                    ) : (
                      <div style={{ fontSize: '0.8rem', color: '#92400e' }}>
                        Compliance Check Required
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Individual Mandatory Requirements */}
              {mandatoryRequirements.map(requirement => (
                <React.Fragment key={requirement.id}>
                  <div style={styles.criteriaCell}>
                    <div style={{ paddingLeft: '1rem', fontSize: '0.9rem', lineHeight: '1.4' }}>
                      {requirement.description}
                    </div>
                  </div>
                  {suppliers.map(supplier => {
                    const evaluation = getMandatoryEvaluation(supplier.id, requirement.id);
                    const cellKey = `mandatory-${supplier.id}-${requirement.id}`;
                    const cellState = cellStates[cellKey] || { isHovered: false, isEditing: false, showTooltip: false };
                    const commentKey = `${supplier.id}-${requirement.id}`;
                    const isCommentExpanded = gridState.expandedComments.has(commentKey);
                    
                    return (
                      <div 
                        key={cellKey}
                        style={{
                          ...styles.scoreCell,
                          background: evaluation?.is_compliant ? '#dcfce7' : '#fef2f2',
                          border: cellState.isHovered ? '2px solid #3b82f6' : '2px solid transparent',
                          cursor: 'pointer',
                          position: 'relative',
                        }}
                        onMouseEnter={() => updateCellState(cellKey, { isHovered: true })}
                        onMouseLeave={() => updateCellState(cellKey, { isHovered: false })}
                        onClick={() => toggleCommentExpansion(supplier.id, requirement.id)}
                      >
                        {/* Compliance Toggle Buttons */}
                        <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '0.5rem' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              updateMandatoryEvaluation(supplier.id, requirement.id, 'is_compliant', true);
                            }}
                            style={{
                              padding: '0.25rem 0.5rem',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '0.7rem',
                              cursor: 'pointer',
                              background: evaluation?.is_compliant ? '#10b981' : '#e5e7eb',
                              color: evaluation?.is_compliant ? 'white' : '#6b7280',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            ✅
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              updateMandatoryEvaluation(supplier.id, requirement.id, 'is_compliant', false);
                            }}
                            style={{
                              padding: '0.25rem 0.5rem',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '0.7rem',
                              cursor: 'pointer',
                              background: !evaluation?.is_compliant ? '#ef4444' : '#e5e7eb',
                              color: !evaluation?.is_compliant ? 'white' : '#6b7280',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            ❌
                          </button>
                        </div>
                        
                        {/* Comment Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCommentExpansion(supplier.id, requirement.id);
                          }}
                          style={{
                            padding: '0.25rem 0.5rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            fontSize: '0.7rem',
                            cursor: 'pointer',
                            background: evaluation?.comments ? '#eff6ff' : 'white',
                            color: evaluation?.comments ? '#2563eb' : '#6b7280',
                            width: '100%',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          {evaluation?.comments ? '💬 Edit' : '💬 Add'}
                        </button>
                        
                        {/* Expanded Comment Section */}
                        {isCommentExpanded && (
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: '-1px',
                            right: '-1px',
                            background: 'white',
                            border: '2px solid #3b82f6',
                            borderRadius: '0 0 8px 8px',
                            padding: '0.75rem',
                            zIndex: 10,
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                          }}>
                            <textarea
                              value={evaluation?.comments || ''}
                              onChange={(e) => updateMandatoryEvaluation(supplier.id, requirement.id, 'comments', e.target.value)}
                              placeholder="Add comments and justification..."
                              rows={3}
                              style={{
                                width: '100%',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                padding: '0.5rem',
                                fontSize: '0.8rem',
                                resize: 'vertical',
                                outline: 'none',
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleCommentExpansion(supplier.id, requirement.id);
                                }}
                                style={{
                                  padding: '0.25rem 0.5rem',
                                  border: '1px solid #6b7280',
                                  borderRadius: '4px',
                                  background: 'white',
                                  color: '#6b7280',
                                  fontSize: '0.7rem',
                                  cursor: 'pointer'
                                }}
                              >
                                Close
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </>
          )}

          {/* Main Criteria Section */}
          {mainCriteria.map((criterion) => (
            <React.Fragment key={criterion.id}>
              {/* Main Criterion Header with collapse functionality */}
              <div 
                style={{ 
                  ...styles.criteriaCell, 
                  background: '#dbeafe', 
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
                onClick={() => toggleCriteriaExpansion(criterion.id)}
                title={`Click to ${gridState.expandedCriteria.has(criterion.id) ? 'collapse' : 'expand'} ${criterion.name}`}
              >
                <span style={{ 
                  marginRight: '0.5rem',
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  color: '#1e40af'
                }}>
                  {gridState.expandedCriteria.has(criterion.id) ? '-' : '+'}
                </span>
                {criterion.name}
                <span style={{ 
                  marginLeft: 'auto', 
                  background: '#3b82f6', 
                  color: 'white', 
                  padding: '0.25rem 0.5rem', 
                  borderRadius: '4px', 
                  fontSize: '0.75rem' 
                }}>
                  {criterion.weight_percentage}%
                </span>
              </div>
              {suppliers.map(supplier => {
                const isCollapsed = gridState.collapsedSuppliers.has(supplier.id);
                
                if (isCollapsed) {
                  // Show criterion completion status for this supplier
                  const criterionScores = subCriterionEvaluations.filter(e => 
                    e.supplier_id === supplier.id && 
                    criterion.sub_criteria.some(sc => sc.id === e.sub_criterion_id)
                  );
                  const completedInCriterion = criterionScores.filter(e => 
                    e.score !== null && e.comments && e.comments.trim() !== ''
                  ).length;
                  const totalInCriterion = criterion.sub_criteria.length;
                  const criterionPercentage = totalInCriterion > 0 ? Math.round((completedInCriterion / totalInCriterion) * 100) : 0;
                  
                  return (
                    <div key={`${supplier.id}-${criterion.id}`} style={{ 
                      ...styles.scoreCell, 
                      background: criterionPercentage === 100 ? '#dcfce7' : '#fef2f2',
                      padding: '0.5rem 0.25rem'
                    }}>
                      <div style={{ fontSize: '0.7rem', color: '#1e40af', fontWeight: 'bold', textAlign: 'center' }}>
                        {criterionPercentage}%
                      </div>
                    </div>
                  );
                } else {
                  return (
                    <div key={`${supplier.id}-${criterion.id}`} style={{ ...styles.scoreCell, background: '#dbeafe' }}>
                      <div style={{ fontSize: '0.8rem', color: '#1e40af', fontWeight: 'bold' }}>
                        Criterion Total
                      </div>
                    </div>
                  );
                }
              })}

              {/* Sub-Criteria - only show if criterion is expanded */}
              {gridState.expandedCriteria.has(criterion.id) && criterion.sub_criteria.map(subCriterion => (
                <React.Fragment key={subCriterion.id}>
                  <div style={{ ...styles.criteriaCell, paddingLeft: '2rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '0.9rem', lineHeight: '1.4' }}>{subCriterion.name}</span>
                      {subCriterion.weight_percentage !== 100 && (
                        <span style={{ 
                          background: '#6b7280', 
                          color: 'white', 
                          padding: '0.25rem 0.5rem', 
                          borderRadius: '4px', 
                          fontSize: '0.75rem',
                          whiteSpace: 'nowrap'
                        }}>
                          {subCriterion.weight_percentage}%
                        </span>
                      )}
                    </div>
                  </div>
                  {suppliers.map(supplier => {
                    const evaluation = getSubCriterionEvaluation(supplier.id, subCriterion.id);
                    const cellKey = `score-${supplier.id}-${subCriterion.id}`;
                    const cellState = cellStates[cellKey] || { isHovered: false, isEditing: false, showTooltip: false };
                    const commentKey = `${supplier.id}-${subCriterion.id}`;
                    const isCommentExpanded = gridState.expandedComments.has(commentKey);
                    const maxScore = process.score_scale === '0-10' ? 10 : 5;
                    
                    return (
                      <div 
                        key={cellKey}
                        style={{
                          ...styles.scoreCell,
                          background: evaluation?.score !== null ? '#f0f9ff' : 'white',
                          border: cellState.isHovered ? '2px solid #3b82f6' : '2px solid transparent',
                          cursor: 'pointer',
                          position: 'relative',
                        }}
                        onMouseEnter={() => updateCellState(cellKey, { isHovered: true })}
                        onMouseLeave={() => updateCellState(cellKey, { isHovered: false })}
                        onClick={() => toggleCommentExpansion(supplier.id, subCriterion.id)}
                      >
                        {/* Score Display/Input */}
                        <div style={{ marginBottom: '0.5rem', textAlign: 'center' }}>
                          {evaluation?.score !== null ? (
                            <div style={{ 
                              fontSize: '1.2rem', 
                              fontWeight: 'bold', 
                              color: '#2563eb',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '0.25rem'
                            }}>
                              {evaluation?.score}
                              <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>/{maxScore}</span>
                            </div>
                          ) : (
                            <select
                              value={evaluation?.score || ''}
                              onChange={(e) => {
                                const score = e.target.value ? parseFloat(e.target.value) : null;
                                updateSubCriterionEvaluation(supplier.id, subCriterion.id, 'score', score);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                width: '100%',
                                padding: '0.25rem',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                fontSize: '0.8rem',
                                background: 'white'
                              }}
                            >
                              <option value="">Score</option>
                              {getScoreDefinitions().map(def => (
                                <option key={def.score} value={def.score}>
                                  {def.score} - {def.title}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                        
                        {/* Comment Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCommentExpansion(supplier.id, subCriterion.id);
                          }}
                          style={{
                            padding: '0.25rem 0.5rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            fontSize: '0.7rem',
                            cursor: 'pointer',
                            background: evaluation?.comments ? '#eff6ff' : 'white',
                            color: evaluation?.comments ? '#2563eb' : '#6b7280',
                            width: '100%',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          {evaluation?.comments ? '💬 Edit' : '💬 Add'}
                        </button>
                        
                        {/* Score Change Buttons (for already scored items) */}
                        {evaluation?.score !== null && (
                          <div style={{ display: 'flex', gap: '0.25rem', marginTop: '0.25rem' }}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                updateSubCriterionEvaluation(supplier.id, subCriterion.id, 'score', null);
                              }}
                              style={{
                                padding: '0.125rem 0.25rem',
                                border: '1px solid #ef4444',
                                borderRadius: '4px',
                                background: 'white',
                                color: '#ef4444',
                                fontSize: '0.6rem',
                                cursor: 'pointer',
                                flex: 1
                              }}
                            >
                              Reset
                            </button>
                          </div>
                        )}
                        
                        {/* Expanded Comment Section */}
                        {isCommentExpanded && (
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: '-1px',
                            right: '-1px',
                            background: 'white',
                            border: '2px solid #3b82f6',
                            borderRadius: '0 0 8px 8px',
                            padding: '0.75rem',
                            zIndex: 10,
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                            minWidth: '250px'
                          }}>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <label style={{ fontSize: '0.7rem', fontWeight: 'bold', color: '#374151', display: 'block', marginBottom: '0.25rem' }}>
                                Comments & Justification:
                              </label>
                              <textarea
                                value={evaluation?.comments || ''}
                                onChange={(e) => updateSubCriterionEvaluation(supplier.id, subCriterion.id, 'comments', e.target.value)}
                                placeholder="Provide detailed justification for your score..."
                                rows={4}
                                style={{
                                  width: '100%',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '4px',
                                  padding: '0.5rem',
                                  fontSize: '0.8rem',
                                  resize: 'vertical',
                                  outline: 'none',
                                  fontFamily: 'inherit'
                                }}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                            {evaluation?.score === null && (
                              <div style={{ marginBottom: '0.5rem' }}>
                                <label style={{ fontSize: '0.7rem', fontWeight: 'bold', color: '#374151', display: 'block', marginBottom: '0.25rem' }}>
                                  Quick Score:
                                </label>
                                <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                                  {getScoreDefinitions().map(def => (
                                    <button
                                      key={def.score}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        updateSubCriterionEvaluation(supplier.id, subCriterion.id, 'score', def.score);
                                      }}
                                      style={{
                                        padding: '0.25rem 0.5rem',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '4px',
                                        background: 'white',
                                        color: '#374151',
                                        fontSize: '0.7rem',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.background = '#f3f4f6';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.background = 'white';
                                      }}
                                    >
                                      {def.score}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleCommentExpansion(supplier.id, subCriterion.id);
                                }}
                                style={{
                                  padding: '0.25rem 0.75rem',
                                  border: '1px solid #6b7280',
                                  borderRadius: '4px',
                                  background: 'white',
                                  color: '#6b7280',
                                  fontSize: '0.7rem',
                                  cursor: 'pointer'
                                }}
                              >
                                Close
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </React.Fragment>
          ))}

          {/* Total Score Row */}
          <div style={{ ...styles.criteriaCell, background: '#dcfce7', fontWeight: 'bold' }}>
            📊 Total Score
          </div>
          {suppliers.map(supplier => {
            // FIXED: Calculate total score properly with correct weighting
            let totalWeightedScore = 0;
            let totalWeightUsed = 0;
            let hasAnyScores = false;
            const maxScore = process.score_scale === '0-10' ? 10 : 5;
            
            mainCriteria.forEach(criterion => {
              let criterionScore = 0;
              let criterionHasScores = false;
              
              if (criterion.sub_criteria.length > 0) {
                // Calculate weighted average within this criterion
                let subCriterionTotalScore = 0;
                let subCriterionTotalWeight = 0;
                
                criterion.sub_criteria.forEach(subCriterion => {
                  const evaluation = getSubCriterionEvaluation(supplier.id, subCriterion.id);
                  if (evaluation?.score !== null && evaluation?.score !== undefined) {
                    subCriterionTotalScore += evaluation.score * (subCriterion.weight_percentage / 100);
                    subCriterionTotalWeight += (subCriterion.weight_percentage / 100);
                    criterionHasScores = true;
                    hasAnyScores = true;
                  }
                });
                
                if (criterionHasScores && subCriterionTotalWeight > 0) {
                  // Criterion average score
                  criterionScore = subCriterionTotalScore / subCriterionTotalWeight;
                }
              }
              
              if (criterionHasScores) {
                // Apply main criterion weight
                totalWeightedScore += criterionScore * (criterion.weight_percentage / 100);
                totalWeightUsed += (criterion.weight_percentage / 100);
              }
            });
            
            // Calculate final score as percentage of max possible
            const finalScore = totalWeightUsed > 0 ? totalWeightedScore / totalWeightUsed : 0;
            const scorePercentage = hasAnyScores ? Math.round((finalScore / maxScore) * 100) : 0;
            const displayScore = hasAnyScores ? finalScore : 0;
            const displayMaxScore = maxScore;
              
            const isCollapsed = gridState.collapsedSuppliers.has(supplier.id);
            
            return (
              <div key={`total-${supplier.id}`} style={{ 
                ...styles.scoreCell, 
                background: '#dcfce7',
                padding: isCollapsed ? '0.5rem 0.25rem' : '0.875rem'
              }}>
                <div style={{ 
                  fontSize: isCollapsed ? '0.8rem' : '1.1rem', 
                  fontWeight: 'bold', 
                  color: '#059669',
                  textAlign: 'center'
                }}>
                  {hasAnyScores ? `${scorePercentage}%` : '--'}
                </div>
                {!isCollapsed && (
                  <div style={{ fontSize: '0.65rem', color: '#065f46', marginTop: '0.25rem' }}>
                    {hasAnyScores ? `${displayScore.toFixed(1)}/${displayMaxScore.toFixed(1)}` : 'No scores yet'}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div style={{ 
        padding: '1rem 2rem', 
        background: 'white', 
        borderTop: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '1rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button
            onClick={onBack}
            style={{
              ...styles.backButton,
              fontSize: '0.9rem',
              padding: '0.75rem 1.5rem'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#4b5563'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#6b7280'}
          >
            ← Back to Process Dashboard
          </button>
          {lastSaved && (
            <span style={{ fontSize: '0.8rem', color: '#059669' }}>
              Last saved: {lastSaved.toLocaleTimeString()}
            </span>
          )}
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button
            onClick={() => saveEvaluations(false)}
            disabled={isSaving}
            style={{
              background: isSaving ? '#9ca3af' : 'linear-gradient(45deg, #f59e0b, #f97316)',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              cursor: isSaving ? 'not-allowed' : 'pointer',
              fontSize: '0.9rem',
              fontWeight: '600',
              transition: 'all 0.2s ease',
            }}
            title='Ctrl+S to save'
          >
            {isSaving ? '💾 Saving...' : '💾 Save Progress'}
          </button>
          
          <button
            onClick={handleCompleteEvaluation}
            disabled={isSaving || evaluationProgress.percentage < 100}
            style={{
              background: evaluationProgress.percentage === 100 
                ? 'linear-gradient(45deg, #10b981, #059669)' 
                : 'linear-gradient(45deg, #9ca3af, #6b7280)',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              cursor: (isSaving || evaluationProgress.percentage < 100) ? 'not-allowed' : 'pointer',
              fontSize: '0.9rem',
              fontWeight: '600',
              transition: 'all 0.2s ease',
              opacity: evaluationProgress.percentage < 100 ? 0.7 : 1
            }}
            onMouseEnter={(e) => {
              if (!isSaving && evaluationProgress.percentage === 100) {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
            title={evaluationProgress.percentage < 100 
              ? `Complete all evaluations to enable this button (${evaluationProgress.percentage}% done)` 
              : 'Complete evaluation - all data validated'}
          >
            {evaluationProgress.percentage === 100 
              ? '✅ Complete Evaluation' 
              : `📊 ${evaluationProgress.percentage}% Complete`}
          </button>
        </div>
      </div>

      {/* Notification */}
      {notification && (
        <div style={{
          position: 'fixed',
          top: '1rem',
          right: '1rem',
          background: notification.type === 'success' ? '#10b981' : '#ef4444',
          color: 'white',
          padding: '1rem 1.5rem',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          zIndex: 1000,
        }}>
          {notification.message}
        </div>
      )}
    </div>
  );
};

export default EvaluationGrid;